import * as React from "react";
import {Button, Grid} from "react-bootstrap";
import {FormattedMessage} from "react-intl";
import {ActionBar} from "../common/ui/actionbar/ActionBar";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import Drawer from '@material-ui/core/Drawer';
import {WithStyles, withStyles} from "@material-ui/core";
import {actions} from "./actions";
import {connect} from "react-redux";
import {RouteComponentProps} from "react-router";
import {AOIMonitor as Notification} from "./model";
import {NotificationsList} from "./NotificationsList";
import {getParameterFromOwnProps} from "../common/util/Util";
import {AOIUserForm} from "./AOIUserForm";
import {selectors} from "./selectors";
import {DeleteAOIUserButton} from "./DeleteAOIUserButton";
import {AxiosError} from "axios";
import {Redirect} from "react-router-dom";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";

interface ServicesPageState {
    canDelete: boolean;
    canCancel: boolean;
    selection: Notification[];
    allowAoiChangeNotifications: boolean;
    error: AxiosError;
}

interface NotificationsPageDispatchProperties {
    refreshData(): Promise<any>;
}

const styles = () => ({
    drawerPaper: {
        width: "50%",
        maxWidth: "570px",
        height: "calc(100% - 75px)",
        top: "auto",
        bottom: 0,
        borderTop: "1px solid rgba(0, 0, 0, 0.12)",
        padding: "15px",
        zIndex: 1040,
    },
});

type NotificationsPageProperties =
    { data: Notification[] }
    & NotificationsPageDispatchProperties
    & WithApiProperties
    & WithStyles<typeof styles>
    & RouteComponentProps;

class NotificationsPageComponent extends React.Component<NotificationsPageProperties, ServicesPageState> {
    constructor(props) {
        super(props);
        this.state = {
            canDelete: false,
            canCancel: false,
            selection: [],
            allowAoiChangeNotifications: false,
            error: null,
        };
    }

    componentDidMount() {
        this.setState({allowAoiChangeNotifications: true});
    }

    private handleRowSelect = (selection: Notification[]) => {
        this.setState({selection});
    };

    render() {
        const {classes, data, refreshData} = this.props;
        const {allowAoiChangeNotifications, error} = this.state;
        const {selection} = this.state;
        const noSelection = selection.length === 0;

        const isCreateAOIUserPanelOpen = this.isCreateAOIUserPanelOpen();
        const aoiUserId = this.getCurrentAoiUserId();
        let notification: Notification;
        if (aoiUserId) {
            notification = data.find(n => n.id === aoiUserId);
        }
        const showPanel = isCreateAOIUserPanelOpen || (!!aoiUserId && !!notification);

        if (error) {
            if (error?.response.status === 401) {
                return <Redirect to="/login"/>;
            }
            return <ErrorDisplay error={error}/>;
        }

        return (
            <Grid fluid={true}>
                <div> {/* This empty div is necessary! it's used by ControlRoomTable to calculate its dimensions.*/}
                    <h3><FormattedMessage id="studio.notifications.notifications-page.notifications"
                                          defaultMessage="Notifications"/></h3>
                    <hr/>
                    <ActionBar>
                        <div>
                            <Button onClick={this.openCreateAOIUserPanel} disabled={!allowAoiChangeNotifications}>
                                <LcdIcon icon={"plus"}/>
                                <FormattedMessage id="studio.notifications.notifications-page.create-aoi-user"
                                                  defaultMessage="Create AOI user"/>
                            </Button>
                        </div>
                        <div>
                            <DeleteAOIUserButton disabled={noSelection || !allowAoiChangeNotifications}
                                                 data={selection}
                                                 onDeleteSuccess={this.onDeleteSuccess}
                                                 onDeleteWarnings={this.clearSelection}/>
                        </div>
                    </ActionBar>
                    {<NotificationsList onRowSelect={this.handleRowSelect} clearSelection={noSelection}/>}
                </div>
                <Drawer anchor={"right"} variant="persistent" classes={{paper: classes.drawerPaper}} open={showPanel}>
                    {showPanel && <AOIUserForm key={aoiUserId} userId={aoiUserId} notification={notification}
                                               onSubmit={this.onAOIUserCreated} onClose={this.closePanel}
                                               refreshData={refreshData} browseAOIs={this.isBrowseAOIsPanelOpen()}/>}
                </Drawer>
            </Grid>
        );
    }

    private onDeleteSuccess = () => {
        this.clearSelection();
    };

    private clearSelection = () => {
        this.handleRowSelect([]);
    };

    private getCurrentAoiUserId = () => {
        const name = getParameterFromOwnProps(this.props, "userId");
        return name ? decodeURIComponent(name) : null;
    };

    private onAOIUserCreated = () => {
        this.closePanel();
        this.props.refreshData();
    }

    private closePanel = () => {
        this.props.history.push("/notifications");
    }

    private openCreateAOIUserPanel = () => {
        this.props.history.push("/notifications/aoi-user/new")
    }

    private isCreateAOIUserPanelOpen() {
        return this.props.location.pathname.endsWith("/new");
    }

    private isBrowseAOIsPanelOpen() {
        return this.props.location.pathname.includes("/aois/");
    }

}

const mapStateToProps = (state) => {
    return {
        data: selectors.getAllNotifications(state),
    };
};

const mapDispatchToProps = (dispatch): NotificationsPageDispatchProperties => {
    return {
        refreshData: () => {
            return dispatch(actions.refreshNotifications());
        },
    };
};

export const NotificationsPage = connect(mapStateToProps, mapDispatchToProps)(
    withStyles(styles)(WithApi(NotificationsPageComponent)));